/**
 * Dot-path utilities for validation issue paths.
 *
 * Paths use dot notation throughout the codebase, matching `BaseIssue.path`:
 * `items.0.amount`, `lines.3.unitPrice`, `supplier.name`. Array indices are
 * plain segments — never bracketed.
 */

/**
 * Path of an issue not anchored to a concrete field — the UI shows it above
 * the document without a scroll-to-field link.
 */
export const BASE_ISSUE_PATH = 'base';

/**
 * Split a dot-path into its segments. An empty path returns an empty array.
 *
 * @example
 * parsePath('lines.1.unitPrice') // ['lines', '1', 'unitPrice']
 * parsePath('amount')            // ['amount']
 * parsePath('')                  // []
 */
export function parsePath(path: string): readonly string[] {
  if (!path) return [];
  return path.split('.');
}

/**
 * `true` if the segment represents an array index — a non-negative integer
 * with no leading zeros (so `'01'` and `'1.0'` are rejected).
 */
export function isIndexSegment(segment: string): boolean {
  if (segment.length === 0) return false;
  const n = Number(segment);
  return Number.isInteger(n) && n >= 0 && String(n) === segment;
}

/**
 * Match a dot-path that points at (or into) the N-th element of an array-typed
 * parent. Returns the parsed `index` and the remaining `child` path inside the
 * element — empty when the path targets the element itself.
 *
 * @example
 * matchIndexedChild('lines.1.unitPrice', 'lines') // { index: 1, child: 'unitPrice' }
 * matchIndexedChild('lines.0',           'lines') // { index: 0, child: '' }
 * matchIndexedChild('totals.gross',      'lines') // null  — different parent
 * matchIndexedChild('lines.foo',         'lines') // null  — non-numeric index
 * matchIndexedChild('lines',             'lines') // null  — no index segment
 */
export function matchIndexedChild(
  path: string,
  parent: string
): { index: number; child: string } | null {
  const segments = parsePath(path);
  if (segments.length < 2 || segments[0] !== parent) return null;
  const idxSegment = segments[1];
  if (!isIndexSegment(idxSegment)) return null;
  return { index: Number(idxSegment), child: segments.slice(2).join('.') };
}
